import React, { useContext, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import MultiChart from './MultiChart';
import { RequestStatus } from '../../Request/types';
import type { SurgeryChartData } from '../types';

interface SurgeryPipelineStatusChartProps {
  data: SurgeryChartData;
  title: string;
}

const SurgeryPipelineStatusChart: React.FC<SurgeryPipelineStatusChartProps> = ({ data, title }) => {
  const { translate } = useContext(LocalizeContext as React.Context<LocalizeContextProps>);

  const categories = useMemo(() => {
    const rows = data.pipelineRows ?? [];
    return rows.map((row) => {
      // SHER-935: the enum is the registry — RequestStatus[n] is what getRequestStatusName
      // returns and what every other status label in the app is built from, so a new status
      // needs no edit here. The numeric fallback stays: `status` arrives typed as a number
      // and the API can ship a member this client does not know yet.
      const name = RequestStatus[row.status as RequestStatus];
      if (!name) {
        return String(row.status);
      }
      return translate(`pages.hospital.services.request_status.${name}`) as string;
    });
  }, [data.pipelineRows, translate]);

  const series = useMemo(
    () => [{ name: title, data: data.series[0]?.data ?? [] }],
    [data.series, title],
  );

  if (!series[0].data.length) {
    return (
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {translate('hospital.analytics.no_data') as string}
        </Typography>
      </Box>
    );
  }

  return (
    <MultiChart
      series={series}
      categories={categories}
      title={title}
      horizontal
      type="bar"
      total={data.count}
    />
  );
};

export default SurgeryPipelineStatusChart;
